/**
 * Offline-first queue for Daily DPR (Daily Progress Report) submissions.
 *
 * Uses IndexedDB so entries persist across page refreshes and full app closes
 * on mobile devices. Each queued item is a PUT to the FC progress endpoint:
 *   PUT /api/v1/projects/{projectId}/fc/activities/{activityId}/progress/{periodId}
 *
 * Public API:
 *   - enqueue({projectId, activityId, periodId, mtdActualQty, remarks}) -> id
 *   - peekAll() -> [item, ...]
 *   - remove(id) -> void
 *   - clear() -> void
 *   - sync(apiClient) -> {synced, failed}
 *   - on(event, cb) / off(event, cb)   events: 'change'
 */

const DB_NAME = 'panorix_dpr_queue';
const DB_VERSION = 1;
const STORE = 'pending';

let _dbPromise = null;
function _openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        os.createIndex('by_created', 'createdAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function _tx(mode = 'readonly') {
  const db = await _openDB();
  const t = db.transaction(STORE, mode);
  return { store: t.objectStore(STORE), done: new Promise((res, rej) => { t.oncomplete = () => res(); t.onerror = () => rej(t.error); }) };
}

const _listeners = new Set();
function _emit() { _listeners.forEach(cb => { try { cb(); } catch (_) {} }); }

export const dprQueue = {
  on(_event, cb) { _listeners.add(cb); },
  off(_event, cb) { _listeners.delete(cb); },

  async enqueue(item) {
    const { store, done } = await _tx('readwrite');
    const record = {
      ...item,
      createdAt: new Date().toISOString(),
      attempts: 0,
      lastError: null,
    };
    const req = store.add(record);
    const id = await new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); });
    await done;
    _emit();
    return id;
  },

  async peekAll() {
    const { store, done } = await _tx('readonly');
    const req = store.getAll();
    const items = await new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); });
    await done;
    return items;
  },

  async remove(id) {
    const { store, done } = await _tx('readwrite');
    store.delete(id);
    await done;
    _emit();
  },

  async clear() {
    const { store, done } = await _tx('readwrite');
    store.clear();
    await done;
    _emit();
  },

  /**
   * Drain the queue by replaying each item against the live API.
   * Returns counts. On per-item failure, increments attempts and records lastError.
   *
   * Each queued item may carry:
   *   - mtdActualQty / remarks → PUT progress endpoint
   *   - photos[] → list of {blob, filename, mimeType} that are multipart-POSTed
   *     to the photos endpoint after the qty PUT succeeds.
   */
  async sync(apiClient) {
    const items = await this.peekAll();
    if (!items.length) return { synced: 0, failed: 0, remaining: 0 };
    let synced = 0, failed = 0;
    for (const item of items) {
      try {
        // 1) Push the qty + remarks
        await apiClient.put(
          `/v1/projects/${item.projectId}/fc/activities/${item.activityId}/progress/${item.periodId}`,
          { mtdActualQty: Number(item.mtdActualQty) || 0, remarks: item.remarks || '' }
        );
        // 2) Push any queued photos (multipart) one-by-one
        const photos = item.photos || [];
        for (const ph of photos) {
          try {
            const fd = new FormData();
            const blob = ph.blob instanceof Blob ? ph.blob : new Blob([ph.blob], { type: ph.mimeType || 'image/jpeg' });
            fd.append('file', blob, ph.filename || 'photo.jpg');
            await apiClient.post(
              `/v1/projects/${item.projectId}/fc/activities/${item.activityId}/progress/${item.periodId}/photos`,
              fd,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
          } catch (_phErr) {
            // photo failed — keep going; we'll retry next sweep
          }
        }
        await this.remove(item.id);
        synced += 1;
      } catch (err) {
        failed += 1;
        try {
          const { store, done } = await _tx('readwrite');
          const get = store.get(item.id);
          get.onsuccess = () => {
            const fresh = get.result;
            if (fresh) {
              fresh.attempts = (fresh.attempts || 0) + 1;
              fresh.lastError = err?.response?.data?.detail || err?.message || 'sync failed';
              store.put(fresh);
            }
          };
          await done;
        } catch (_) { /* ignore */ }
      }
    }
    _emit();
    const remaining = (await this.peekAll()).length;
    return { synced, failed, remaining };
  },
};
